import { useMemo } from 'react';
import {
  booleanFacets,
  facetsWithDisplayName,
  rangeFacets
} from '~/constants/common/facets';
import TagIds from '~/constants/common/tagIds';
import type {
  AppliedFilters,
  AvailabilityType,
  FacetData,
  FacetLists,
  FacetRanges,
  FacetTag,
  FinanceType,
  MakeType,
  ModelType,
  ModelsKey,
  RangeFacet
} from '~/contracts/common';
import { isDefined } from '~/utilities/array';
import addDaysToDate from '~/utilities/date/addDaysToDate';
import getFullDate from '~/utilities/date/getFullDate';
import getWeekDay from '~/utilities/date/getWeekDay';
import formatAndDropDecimal from '~/utilities/formatAndDropDecimal';
import { logError } from '~/utilities/loggers';

// we use the duplicate trims to decide if we need to show "<Model> <Trim>" or just "<Trim>" for trim tags
const getDuplicateTrims = (makes: MakeType[]): string[] => {
  const allTrims =
    makes.flatMap(make => make?.models?.flatMap(model => model?.trims)) ?? [];

  return allTrims
    .filter(
      (trim, index, array) =>
        array.indexOf(trim) !== index && array.lastIndexOf(trim) === index
    )
    .filter(isDefined);
};

const getAvailabilityTag = (
  facetKey: 'earliestDayToDeliveryOrPickup',
  values: AvailabilityType
): FacetTag => {
  const day = addDaysToDate(values.days);
  const date = getFullDate(day);

  const tag: FacetTag = {
    context: { key: facetKey },
    facetKey,
    tagName: ''
  };

  const getItTomorrow = values.days === 1;
  if (getItTomorrow) tag.tagName = 'Get It By Tomorrow';

  const getItByDay = values.days > 1 && values.days < 7;
  if (getItByDay) tag.tagName = `Get it by ${getWeekDay(day)}`;

  const getItByDate = values.days > 7;
  if (getItByDate) tag.tagName = `Get it by ${date}`;

  return tag;
};

function getMakeModelTrimTags(
  facetKey: 'makes',
  values: MakeType[]
): FacetTag[] {
  const duplicateTrims = getDuplicateTrims(values);

  return values.flatMap(make => {
    const makeData: FacetTag = {
      context: {
        key: make.name
      },
      facetKey,
      tagName: make.name
    };

    const modelsKey: ModelsKey = make.models ? 'models' : 'parentModels';
    const models = make[modelsKey] ?? [];

    const modelData = models.map<FacetTag>(model => ({
      context: {
        key: model.name,
        modelsKey,
        parents: [make.name]
      },
      facetKey,
      tagName: model.name
    }));

    const trimData = models.flatMap<FacetTag>((model: ModelType) =>
      (model.trims ?? []).map<FacetTag>(trim => ({
        context: {
          key: trim,
          modelsKey,
          parents: [make.name, model.name]
        },
        facetKey,
        tagName: duplicateTrims.includes(trim) ? `${model.name} ${trim}` : trim
      }))
    );

    return [makeData, ...modelData, ...trimData];
  });
}

type Range =
  | {
      type: 'equal';
      value: number;
    }
  | {
      type: 'extreme';
      side: 'min' | 'max';
      value: number;
    }
  | {
      type: 'range';
      value: Required<RangeFacet>;
    };

function getRange(value: RangeFacet): Range {
  if (value.min == null || value.max == null) {
    return {
      type: 'extreme',
      side: value.min ? 'min' : 'max',
      value: value.min ?? value.max ?? 0
    };
  }

  if (value.min === value.max) {
    return { type: 'equal', value: value.min };
  }

  return {
    type: 'range',
    value: { max: value.max, min: value.min }
  };
}

function getRangeFilterTag(
  facetKey: keyof FacetRanges | 'finance',
  value: RangeFacet & FinanceType
): FacetTag {
  const range = getRange(value);

  switch (facetKey) {
    case 'finance': {
      const tagName = (function () {
        const { downPayment, monthlyPayment } = value;

        if (downPayment != null && monthlyPayment) {
          return `$${formatAndDropDecimal(
            downPayment
          )} Down & $${formatAndDropDecimal(monthlyPayment)}/mo.`;
        } else if (downPayment != null) {
          return `$${formatAndDropDecimal(downPayment)} Down`;
        } else if (monthlyPayment) {
          return `$${formatAndDropDecimal(monthlyPayment)}/mo.`;
        } else {
          return '';
        }
      })();

      return {
        context: { key: facetKey },
        facetKey,
        tagName
      };
    }

    case 'mileage':
      return {
        context: { key: facetKey },
        facetKey,
        tagName: (function () {
          switch (range.type) {
            case 'equal':
              return `${range.value.toLocaleString()} miles`;
            case 'extreme':
              return range.side === 'min'
                ? `${range.value.toLocaleString()} miles or more`
                : `Under ${range.value.toLocaleString()} miles`;
            case 'range':
              return `${range.value.min.toLocaleString()} - ${range.value.max.toLocaleString()} miles`;
          }
        })()
      };

    case 'mpg':
      return {
        context: { key: facetKey },
        facetKey,
        tagName: (function () {
          switch (range.type) {
            case 'equal':
              return `${range.value.toLocaleString()} MPG`;
            case 'extreme':
              return range.side === 'min'
                ? `${range.value.toLocaleString()} MPG or better`
                : `Up to ${range.value.toLocaleString()} MPG`;
            case 'range':
              return `${range.value.min.toLocaleString()} - ${range.value.max.toLocaleString()} MPG`;
          }
        })()
      };

    case 'price': {
      return {
        context: { key: facetKey },
        facetKey,
        tagName: (function () {
          switch (range.type) {
            case 'equal':
              return `$${formatAndDropDecimal(range.value)}`;
            case 'extreme':
              return range.side === 'min'
                ? `$${formatAndDropDecimal(range.value)} and up`
                : `Under $${formatAndDropDecimal(range.value)}`;
            case 'range':
              return `$${formatAndDropDecimal(
                range.value.min
              )} - $${formatAndDropDecimal(range.value.max)}`;
          }
        })()
      };
    }

    case 'seatingCapacity':
      return {
        context: { key: facetKey },
        facetKey,
        tagName: (function () {
          switch (range.type) {
            case 'equal':
              return `${range.value} seats`;
            case 'extreme':
              return range.side === 'min'
                ? `${range.value} or more seats`
                : `Up to ${range.value} seats`;
            case 'range':
              return `${range.value.min} - ${range.value.max} seats`;
          }
        })()
      };

    case 'towingCapacity':
      return {
        context: { key: facetKey },
        facetKey,
        tagName: (function () {
          switch (range.type) {
            case 'equal':
              return `${range.value.toLocaleString()} Towing Capacity`;
            case 'extreme':
              return range.side === 'min'
                ? `Over ${range.value.toLocaleString()} Towing Capacity`
                : `Under ${range.value.toLocaleString()} Towing Capacity`;
            case 'range':
              return `${range.value.min.toLocaleString()} - ${range.value.max.toLocaleString()} Towing Capacity`;
          }
        })()
      };

    case 'year':
      return {
        context: { key: facetKey },
        facetKey,
        tagName: (function () {
          switch (range.type) {
            case 'equal':
              return `${range.value}`;
            case 'extreme':
              return range.side === 'min'
                ? `${range.value} or newer`
                : `${range.value} or older`;
            case 'range':
              return `${range.value.min} - ${range.value.max}`;
          }
        })()
      };

    default:
      return {
        context: { key: facetKey },
        facetKey,
        tagName: ''
      };
  }
}

const getDisplayNameFromFacetData = (
  facetData: Partial<FacetData>,
  facetKey: keyof FacetLists | 'isFreeDelivery' | 'tagIds',
  value: any
): FacetTag[] => {
  if (facetKey === 'cvnaFeatures') {
    return value.map((feature: string) => ({
      context: { key: feature },
      facetKey,
      tagName: feature
    }));
  }

  if (facetKey === 'isFreeDelivery') {
    return value
      ? [
          {
            context: { key: facetKey },
            facetKey,
            tagName: facetData?.waysToSave?.isFreeDelivery?.displayName ?? ''
          }
        ]
      : [];
  }

  if (!Array.isArray(value)) {
    return [];
  }
  //TODO: This is a temporary fix for the EV Credit tag. We need to update the backend to return the correct tag name

  return (
    facetData[facetKey]?.buckets
      ?.filter(v => value.includes(v.key))
      ?.map<FacetTag>(v => ({
        context: { key: v.key },
        tagName:
          v.key === TagIds.EvTaxCreditMayBeEligible.toString()
            ? 'Clean Vehicle Tax Credit'
            : v.displayName,
        facetKey
      })) ?? []
  );
};

/**
 * We use this to get the filter tags for each applied filter.
 * To do this we loop through facet data and for each applied filter
 * we either return the displayName or build a tag for it.
 * The only tags we need to build still are Range tags.
 */
export default function useGetFilterTags(
  baseline: Partial<FacetData>,
  filters: AppliedFilters,
  shouldGenerate: boolean = true
): FacetTag[] {
  return useMemo<FacetTag[]>(() => {
    if (!shouldGenerate) return [];

    const getTags = (
      tags: FacetTag[],
      [facetKey, value]: [any, any]
    ): FacetTag[] => {
      try {
        const isEmpty =
          Object.keys(value).length === 0 && !booleanFacets.includes(facetKey);
        if (isEmpty) return tags;

        if (facetsWithDisplayName.includes(facetKey)) {
          const tagsFromFacetData = getDisplayNameFromFacetData(
            baseline,
            facetKey,
            value
          );

          return [...tags, ...(tagsFromFacetData ?? [])];
        }

        if (facetKey === 'earliestDayToDeliveryOrPickup') {
          const availabilityTag = getAvailabilityTag(facetKey, value);

          return [...tags, availabilityTag];
        }

        if (rangeFacets.includes(facetKey)) {
          const rangeTag = getRangeFilterTag(facetKey, value);

          return [...tags, rangeTag];
        }

        if (facetKey === 'makes') {
          const makesTags = getMakeModelTrimTags(facetKey, value) ?? [];

          return [...tags, ...makesTags];
        }

        return tags;
      } catch (e) {
        logError(new Error('Failed to generate filter tags'), {
          location: 'useGetFilterTags',
          facetKey,
          value
        });

        return [];
      }
    };

    const filterTags = Object.entries(filters ?? {}).reduce(getTags, []);

    return filterTags ?? [];
  }, [baseline, filters, shouldGenerate]);
}
